import { getToken } from "../data/storage/authStorage";
import { API_BASE_URL } from "../../config/api";

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

/** Action type for closed-month review requests. Include in action_types alongside the specific action (e.g. edit, push_to_zoho). */
export const ACTION_TYPE_MONTH_CLOSE = "month_close";

/** Staged Excel rows for customer/vendor `bulk_upload` reviews (`change_details.raw_json`). */
export interface BulkUploadReviewRawJson {
  kind: "customer_bulk" | "vendor_bulk" | "gl_bulk";
  company_gst_enabled?: number;
  template_version?: string;
  rows: Array<{ excel_row: number; payload: Record<string, unknown> }>;
}

/**
 * Change details structure for review requests
 */
export interface ChangeDetails {
  /** When true, this request is for a change in a closed month (post-close review). */
  closed_month?: boolean;
  old_value?: string;
  new_value?: string;
  reason?: string;
  field_changes?: Array<{
    field: string;
    old: string;
    new: string;
  }>;
  // Raw JSON of the complete document (invoice/bill/journal) with all changes applied
  // This is used for applying changes, not for display
  raw_json?: any; // InvoiceDataEntity | BillDataEntity | Journal data structure
  // For customer/vendor bulk_upload reviews, raw_json may instead match BulkUploadReviewRawJson.
  // Original JSON of the document before changes (for journals, used to get old account/contact names)
  original_json?: any; // Original document state before changes
  /**
   * Post-validation baseline for invoice/bill review display diffs only.
   * When set, header `tax_total` / `sub_total` / `total` and line items compare from this snapshot
   * instead of `original_json` so system-driven tax/TDS recalculations are not shown as user edits.
   */
  validation_snapshot?: {
    line_items?: unknown[] | null;
    tax_total?: number;
    sub_total?: number;
    total?: number;
  };
  // For GL override reviews: store line item index and account details
  line_item_index?: number; // Index of the line item being overridden
  line_item_gl_data?:
    | {
        old_account_id?: string;
        old_account_name?: string;
        new_account_id?: string;
        new_account_name?: string;
      }
    | Array<{
        line_item_index: number;
        old_account_id: string;
        old_account_name: string;
        new_account_id: string;
        new_account_name: string;
        change_scope?: "always" | "once";
        reason?: string;
        override_data?: any;
      }>; // Support both single object (backward compatibility) and array (multiple GL overrides)
  change_scope?: "always" | "once"; // For GL override: whether it's permanent or one-time
  // For multiple actions requiring review: array of action codes in snake_case (e.g., ["create_recurring", "push_to_zoho"])
  action_types?: string[]; // Array of action codes in snake_case, used in approval screen to show which actions are being reviewed
  // For GL override reviews: store the semantic override request data
  override_data?: any; // SemanticOverrideRequest structure for creating semantic override when review is approved
  // For credit notes: store original credit note data with credits_applied
  original_credit_note?: {
    credit_note: any; // The credit note entity from API response
    credits_applied: any; // The credits_applied data from API response
  };
}

/**
 * Payload for logging direct approve/update actions with mandatory reason.
 */
export interface ApprovalAuditPayload {
  reason: string;
  change_details: ChangeDetails;
}

/**
 * Review Request DTO
 */
export interface CreateReviewRequestDTO {
  module_code: string;
  action_code: string;
  entity_type: string;
  entity_id: number;
  request_comments: string; // Required
  change_details?: ChangeDetails; // Optional structured change data
}

/**
 * Review Request Response
 */
export interface CreateReviewResponseDTO {
  success: boolean;
  data: {
    review_id: number;
  };
  message: string;
}

/**
 * Check Approved Review Query Params
 */
export interface CheckApprovedReviewParams {
  user_id: number;
  entity_type: string;
  entity_id: number;
  action_code: string;
}

/**
 * Check Approved Review Response
 */
export interface CheckApprovedReviewResponseDTO {
  success: boolean;
  data: {
    approved: boolean;
    review: {
      id: number;
      status: "approved" | "rejected" | "pending";
      reviewed_at: string;
      review_comments?: string;
      is_applied?: boolean;
    } | null;
  };
  message: string;
}

/**
 * Mark Review As Applied Response
 */
export interface MarkReviewAsAppliedResponseDTO {
  success: boolean;
  data: {
    applied: boolean;
  };
  message: string;
}

/**
 * Create review request
 * @param data - Review request data including required comments
 * @returns Review request response with review_id
 */
export const createReviewRequest = async (
  data: CreateReviewRequestDTO,
): Promise<CreateReviewResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const url = `${API_BASE_URL}/api/reviews`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      500,
    );
  }
};

/**
 * Check if entity has approved review
 * @param params - Query parameters (user_id is added automatically from token)
 * @returns Approval status and review details if approved
 */
export const checkApprovedReview = async (
  params: Omit<CheckApprovedReviewParams, "user_id">,
): Promise<CheckApprovedReviewResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  // Get user_id from token (you may need to decode JWT or get from user context)
  // For now, we'll get it from the API call - the backend will extract it from token
  // But we need user_id in query params, so we'll need to get it from somewhere
  // Let's check if there's a user context or we need to pass it
  const userData = localStorage.getItem("user_data");
  const user = userData ? JSON.parse(userData) : null;
  const userId = user?.id;

  if (!userId) {
    throw new APIError("User ID not found", 401);
  }

  const queryParams = new URLSearchParams({
    user_id: userId.toString(),
    entity_type: params.entity_type,
    entity_id: params.entity_id.toString(),
    action_code: params.action_code,
  });

  const url = `${API_BASE_URL}/api/reviews/check-approved?${queryParams.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      500,
    );
  }
};

/**
 * Mark review as applied
 * @param reviewId - Review ID to mark as applied
 * @returns Response indicating success
 */
export const markReviewAsApplied = async (
  reviewId: number,
): Promise<MarkReviewAsAppliedResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const url = `${API_BASE_URL}/api/reviews/${reviewId}/mark-applied`;

  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      500,
    );
  }
};
